import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  AlertTriangle,
  Zap,
  ListChecks,
  History,
} from "lucide-react";

type StageLike = { id?: string | number; name?: string } | null | undefined;
type StatusLike = { id?: string | number; name?: string } | null | undefined;

interface ConfirmStageMoveDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Stage the lead is currently on (server state). */
  currentStage: StageLike;
  /** Stage the lead will be on after confirm. */
  targetStage: StageLike;
  /** Status the lead will land on after confirm. */
  targetStatus?: StatusLike;
  /** Optional sub-status the lead will land on after confirm. */
  targetSubStatus?: StatusLike;
  /**
   * Visual + copy variant.
   * - "next": "Move to next stage" (footer pipeline button)
   * - "skip": user picked a non-adjacent forward stage (intermediates marked Finished)
   * - "rollback": going to an earlier stage
   * - "update": same stage, status/sub-status change only
   */
  mode?: "next" | "skip" | "rollback" | "update";
  isPending?: boolean;
  onConfirm: () => void;
  /** Override the confirm button label. */
  confirmLabel?: string;
}

function deriveMode(
  current: StageLike,
  target: StageLike,
  fallback: ConfirmStageMoveDialogProps["mode"]
): NonNullable<ConfirmStageMoveDialogProps["mode"]> {
  if (fallback) return fallback;
  if (!current?.id || !target?.id) return "update";
  if (String(current.id) === String(target.id)) return "update";
  return "next";
}

export default function ConfirmStageMoveDialog({
  open,
  onOpenChange,
  currentStage,
  targetStage,
  targetStatus,
  targetSubStatus,
  mode,
  isPending = false,
  onConfirm,
  confirmLabel,
}: ConfirmStageMoveDialogProps) {
  const resolvedMode = deriveMode(currentStage, targetStage, mode);

  const titleByMode: Record<typeof resolvedMode, string> = {
    next: `Move lead to "${targetStage?.name ?? "next stage"}"?`,
    skip: `Skip stages and move to "${targetStage?.name ?? "selected stage"}"?`,
    rollback: `Roll back to "${targetStage?.name ?? "previous stage"}"?`,
    update: `Update status on "${currentStage?.name ?? "current stage"}"?`,
  };

  const intentByMode: Record<typeof resolvedMode, string> = {
    next: "Confirm before the lead jumps stage. This is logged on the activity timeline and may trigger automations bound to the new stage.",
    skip: "All stages between the current and the chosen one will be marked Finished. The change is logged as a manual update.",
    rollback:
      "The lead will be sent back to an earlier stage. Stage history is preserved on the activity log.",
    update:
      "Status / sub-status will be updated on the current stage. Automations listening for status changes may run.",
  };

  const consequences: Array<{
    icon: React.ReactNode;
    title: string;
    body: string;
  }> = [
    {
      icon: <Zap className="size-3.5 text-[#b45309]" strokeWidth={2.25} />,
      title: "Automations may run",
      body: "Any automation bound to this transition (notifications, follow-ups, assignments, webhooks) will fire automatically.",
    },
    {
      icon: <History className="size-3.5 text-[#156548]" strokeWidth={2.25} />,
      title: "Activity log entry",
      body: "This change is captured on the lead's timeline with your name as the actor.",
    },
    ...(resolvedMode === "skip"
      ? [
          {
            icon: (
              <ListChecks
                className="size-3.5 text-[#7c3aed]"
                strokeWidth={2.25}
              />
            ),
            title: "Skipped stages will be marked Finished",
            body: "Stages between the current and the chosen one are closed without manual intervention.",
          },
        ]
      : []),
    ...(resolvedMode !== "update"
      ? [
          {
            icon: (
              <AlertTriangle
                className="size-3.5 text-[#d97706]"
                strokeWidth={2.25}
              />
            ),
            title: "Status & sub-status reset",
            body: "If you didn't pick one explicitly, the lead lands on the first status of the new stage.",
          },
        ]
      : []),
  ];

  const fromLabel = currentStage?.name ?? "—";
  const toLabel = targetStage?.name ?? "—";
  const sameStage =
    currentStage?.id != null &&
    targetStage?.id != null &&
    String(currentStage.id) === String(targetStage.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] rounded-[20px] p-0 gap-0">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-[#e6e8e7]">
          <p className="text-[16px] font-medium text-black tracking-[-0.4px] leading-snug">
            {titleByMode[resolvedMode]}
          </p>
          <p className="text-[12.5px] text-[#697571] tracking-[-0.2px] leading-snug mt-1">
            {intentByMode[resolvedMode]}
          </p>
        </DialogHeader>

        <div className="px-5 py-4 flex flex-col gap-4">
          <div className="flex items-center gap-2 rounded-[10px] border border-[#e6e8e7] bg-[#f6fbf8] px-3 py-2.5">
            <div className="flex flex-col min-w-0">
              <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#697571]">
                From
              </span>
              <span className="text-[13px] font-semibold text-[#1e302a] truncate">
                {fromLabel}
              </span>
            </div>
            <ArrowRight className="size-4 text-[#156548] mx-2 shrink-0" />
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#156548]">
                {sameStage ? "Stays on" : "To"}
              </span>
              <span className="text-[13px] font-semibold text-[#0a3a28] truncate">
                {toLabel}
              </span>
            </div>
          </div>

          {(targetStatus?.name || targetSubStatus?.name) && (
            <div className="grid grid-cols-2 gap-2">
              {targetStatus?.name && (
                <div className="rounded-[10px] border border-[#e6e8e7] px-3 py-2 bg-white">
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#697571]">
                    Status
                  </p>
                  <p className="text-[12.5px] font-medium text-[#1e302a] truncate">
                    {targetStatus.name}
                  </p>
                </div>
              )}
              {targetSubStatus?.name && (
                <div className="rounded-[10px] border border-[#e6e8e7] px-3 py-2 bg-white">
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#697571]">
                    Sub-status
                  </p>
                  <p className="text-[12.5px] font-medium text-[#1e302a] truncate">
                    {targetSubStatus.name}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#697571]">
              What happens next
            </p>
            <ul className="flex flex-col gap-1.5">
              {consequences.map((c) => (
                <li
                  key={c.title}
                  className="flex items-start gap-2 rounded-[8px] border border-[#eef0ef] bg-[#fcfcfc] px-2.5 py-2"
                >
                  <span className="mt-0.5 shrink-0">{c.icon}</span>
                  <div className="flex flex-col leading-snug">
                    <span className="text-[12px] font-semibold text-[#1e302a] tracking-[-0.2px]">
                      {c.title}
                    </span>
                    <span className="text-[11.5px] text-[#505e59] tracking-[-0.18px]">
                      {c.body}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <DialogFooter className="px-5 pb-4 pt-2 border-t border-[#e6e8e7] flex justify-end gap-2">
          <Button
            type="button"
            variant="cancel"
            className="w-[140px]"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={isPending}
            onClick={onConfirm}
          >
            {isPending
              ? "Working..."
              : (confirmLabel ??
                (resolvedMode === "rollback"
                  ? "Roll back lead"
                  : resolvedMode === "update"
                    ? "Update lead"
                    : "Move lead"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
